const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.earmiss.ru';

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('accessToken');
  if (token && token !== 'undefined') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken || refreshToken === 'undefined') {
    return null;
  }

  try {
    const response = await fetch(`${BASE_URL}/admin/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      const newAccessToken = data.accessToken || data.access_token || data.token;
      if (newAccessToken) {
        localStorage.setItem('accessToken', newAccessToken);
        return newAccessToken;
      }
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }
  
  // If refresh fails, clear stored tokens
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  return null;
};

const handleResponse = async (response) => {
  if (response.status === 401) {
    return { success: false, error: 'Unauthorized', status: 401 };
  }
  
  try {
    const data = await response.json();
    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || 'Something went wrong',
        status: response.status
      };
    }
    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Network response was not json' };
  }
};

const request = async (endpoint, options = {}) => {
  try {
    let response = await fetch(`${BASE_URL}/admin${endpoint}`, {
      ...options,
      headers: {
        ...getHeaders(),
        ...options.headers,
      },
    });

    // If 401, try to refresh token and retry
    if (response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        const newToken = await refreshAccessToken();
        isRefreshing = false;

        if (newToken) {
          onRefreshed(newToken);
          // Retry the original request with new token
          response = await fetch(`${BASE_URL}/admin${endpoint}`, {
            ...options,
            headers: {
              ...getHeaders(),
              ...options.headers,
            },
          });
        } else {
          return { success: false, error: 'Unauthorized', status: 401 };
        }
      } else {
        // If already refreshing, wait for the refresh to complete
        return new Promise(resolve => {
          addRefreshSubscriber(token => {
            fetch(`${BASE_URL}/admin${endpoint}`, {
              ...options,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...options.headers,
              },
            })
              .then(handleResponse)
              .then(resolve);
          });
        });
      }
    }

    return handleResponse(response);
  } catch (error) {
    console.error('API Request Error:', error);
    return { success: false, error: error.message };
  }
};

export const RealAPI = {
  auth: {
    login: async (username, password) => {
      return request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
    },

    refreshToken: async (refreshToken) => {
      return request('/auth/refresh-token', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    },

    logout: async () => {
      // Backend logout (optional, depending on implementation)
      // We still call it to be safe
      return request('/auth/logout', {
        method: 'POST',
      });
    }
  },

  users: {
    getAll: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return request(`/users?${queryParams}`);
    },
    
    getById: async (id) => {
      return request(`/users/${id}`);
    },

    getUserSummaries: async (userId, page = 1, limit = 5) => {
      return request(`/users/${userId}/summaries?page=${page}&limit=${limit}`);
    },

    // Not listed in required endpoints but keeping signature
    addHours: async (userId, hours, reason) => {
      return request(`/users/${userId}/hours`, {
        method: 'POST',
        body: JSON.stringify({ hours, reason }),
      });
    },

    // Not listed, implementing as client-side filter or separate endpoint if available.
    // The mock implemented search. If backend doesn't support search, we might need to fetch all and filter.
    // Assuming backend might support ?search=... or we just filter client side if not provided.
    // User didn't list search endpoint.
    // For safety, I'll stick to getAll() if search is not supported, OR fetch all and filter.
    // Since users list might be large, real backend usually has search.
    // I'll assume /users?q=... or similar, but the user spec didn't mention it.
    // I'll leave it as a TODO or implement naive getAll for now.
    search: async (query, limit = 5) => {
      return request(`/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    }
  },

  notifications: {
    testSend: async (payload) => {
      return request('/notifications/test', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },

    start: async (payload) => {
      return request('/notifications', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },

    status: async (notificationId) => {
      const result = await request(`/notifications/${notificationId}/status`);
      if (!result.success) {
        return result;
      }

      return {
        ...result,
        data: result.data?.data || null
      };
    },

    list: async (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return request(`/notifications${queryParams ? `?${queryParams}` : ''}`);
    },

    getById: async (notificationId) => {
      return request(`/notifications/${notificationId}`);
    }
  },

  payments: {
    // Hidden in UI, but preserving structure
    getByUserId: async (userId) => {
      return request(`/users/${userId}/payments`); // Assumption
    },

    getAll: async () => {
      return request('/finance/history'); // User mentioned this endpoint for future
    }
  },

  stats: {
    getOverview: async () => {
      return request('/stats/summaries-overview');
    },

    getStats: async () => {
      return request('/stats');
    },

    getIncomeChart: async (period = 'week') => {
      return request(`/stats/income-chart?period=${period}`);
    },

    getSubscriptionDistribution: async () => {
      return request('/stats/subscription-distribution');
    },

    getSummariesOverview: async () => {
      return request('/stats/summaries-overview');
    },

    getSummariesChart: async () => {
      return request('/stats/summaries-chart');
    },

    getUsersChart: async () => {
      return request('/stats/users-chart');
    },

    getRecentSummaries: async (page = 1, limit = 10) => {
      return request(`/summaries/recent?limit=${limit}`);
    },

    getAudioDuration: async () => {
      return request('/stats/audio-duration');
    },

    getModelsReactions: async () => {
      return request('/stats/summaries-models');
    }
  }
};

export default RealAPI;
